import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Typewriter, SectionLabel, CTA, FloatingActions } from './Home';
import { PageHero } from './Services';
import DotConnect from '../components/DotConnect';
import BlueprintHero from '../components/BlueprintHero';
import ServiceBackdrop from '../components/ServiceBackdrop';
import useSeo from '../hooks/useSeo';
import {
  SURVEY_TYPES,
  SURVEY_PROCESS,
  SURVEY_GETTING_STARTED,
  SURVEY_TECH_STACK,
  SURVEY_DATA_INPUTS,
  SURVEY_INDUSTRIES,
  SURVEY_PROJECTS
} from '../mock/surveyTypes';

const Icon = ({ name, ...p }) => {
  const Cmp = Icons[name] || Icons.Square;
  return <Cmp {...p} />;
};

const HERO_FEATURES = [
  { title: 'CAD Drafting Only',  desc: 'No field work — we draft your survey data', icon: 'PenTool' },
  { title: 'ALTA / Boundary / Topo', desc: 'All survey types drafted to your standards', icon: 'Layers' },
  { title: '24–48hr Turnaround', desc: 'Fast delivery on every project',             icon: 'Timer' },
  { title: 'White-Label Ready',  desc: 'Confidential drafts for your seal',          icon: 'Stamp' }
];

const WHY_MATTERS = [
  { num: '01', title: 'Property Transactions',  desc: 'Clean boundary CAD drafts protect ownership rights during closings.' },
  { num: '02', title: 'Permit & Plat Approvals', desc: 'Recording-ready plats and exhibits aligned to local jurisdiction standards.' },
  { num: '03', title: 'Risk Reduction',         desc: 'Precision drafting prevents costly construction and legal errors.' },
  { num: '04', title: 'Engineering Hand-off',   desc: 'Survey-grade base maps for civil, architectural, and BIM design teams.' }
];

const WHY_CHOOSE = [
  { title: 'Accurate & On-Time',   desc: 'Survey-grade CAD drafts delivered in 24–48 hours from your field data.',   icon: 'CheckCircle2' },
  { title: 'Surveyor-First Support', desc: 'Built by drafters who speak your language — field codes, descriptions, and templates.', icon: 'Headphones' },
  { title: 'Smart & Efficient',    desc: 'Clean linework, layered to your standards, ready for your seal.',          icon: 'Rocket' },
  { title: 'Cost-Effective',       desc: 'Outsourced CAD drafting that frees your crews to stay in the field.',     icon: 'PiggyBank' },
  { title: 'Tailored Expertise',   desc: 'Civil 3D, Carlson, MicroStation — we draft in your platform of choice.',   icon: 'Wrench' },
  { title: 'White-Label Ready',    desc: 'Confidential, branded deliverables you sign and send to your client.',     icon: 'Stamp' }
];

export const LandSurveyingPage = () => {
  useSeo({
    title: 'Land Survey CAD Drafting Services',
    description: 'Topographic, boundary, ALTA/NSPS, as-built and route survey CAD drafting — white-label deliverables in 24-48 hours from Land Drafters.',
    path: '/services/land-surveying'
  });
  return (
    <>
      {/* HERO */}
      <section className="relative pt-20 pb-12 md:pt-24 md:pb-16 bg-[#0e1116] text-white overflow-hidden" data-testid="ls-hero">
        <DotConnect density={50} color="#94a3b8" lineColor="rgba(148,163,184,0.85)" accentColor="#f97316" maxDistance={130} />
        <ServiceBackdrop variant="survey" accent="#94a3b8" accentAlt="#f97316" />
        <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(148,163,184,0.2),transparent_60%)] pointer-events-none"></div>
        <div className="relative max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link to="/services" className="inline-flex items-center gap-1 text-green-400 text-xs font-bold uppercase tracking-wider mb-5 hover:text-orange-400">
              <Icons.ArrowLeft className="w-3.5 h-3.5" /> All Services
            </Link>
            <div className="inline-flex items-center gap-2 bg-orange-900/40 border border-orange-700/50 text-orange-300 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded mb-4">
              <Icons.PenTool className="w-4 h-4" /> CAD Drafting Only · We Do Not Perform Field Surveys
            </div>
            <h1 className="text-4xl md:text-6xl font-black leading-[1.05]">
              Land Survey <span className="text-green-500">CAD Drafting</span>
            </h1>
            <div className="mt-3 text-lg md:text-2xl font-bold">
              <Typewriter words={['ALTA / NSPS', 'Boundary', 'Topographic', 'As-Built', 'Subdivision', 'Pipeline & ROW']} className="text-orange-500" />
            </div>
            <p className="text-gray-300 mt-6 max-w-xl leading-relaxed">
              Outsourced CAD drafting services <strong className="text-white">for land surveying professionals, firms, and engineers.</strong> We process your field data into precision deliverables — <strong className="text-orange-300">we do not perform field surveys</strong>.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-5">
              {['Trusted by Licensed Surveyors', 'NDA Protected', 'White-Label Ready', 'ALTA / NSPS Compliant'].map(t => (
                <span key={t} className="inline-flex items-center gap-1.5 bg-gray-900/30 border border-gray-700/40 text-green-200 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full">
                  <Icons.CheckCircle2 className="w-3 h-3 text-orange-400" /> {t}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/get-quote" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider" data-testid="ls-quote-btn">
                Get a Quote <Icons.ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-500 hover:border-green-500 hover:text-green-400 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider">
                Contact Us
              </Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <BlueprintHero slug="land-surveying" accent="#94a3b8" accentAlt="#f97316" />
          </div>
        </div>
      </section>

      {/* WE DO / WE DON'T */}
      <section className="py-16 bg-white" data-testid="ls-scope">
        <div className="max-w-[1200px] mx-auto px-6 grid md:grid-cols-2 gap-6">
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-7">
            <div className="text-green-700 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2 mb-3">
              <Icons.CheckCircle2 className="w-4 h-4" /> What We Do
            </div>
            <h3 className="text-2xl font-black text-[#0e1116]">CAD Drafting From Your Field Data</h3>
            <p className="text-gray-700 mt-3 leading-relaxed">
              We are an outsourced CAD drafting partner for licensed surveyors and survey firms. Send us your point files, field notes, sketches, and raw data — we deliver clean, layered, sealed-ready DWG / DXF / PDF files in 24–48 hours.
            </p>
          </div>
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-7">
            <div className="text-orange-700 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2 mb-3">
              <Icons.XCircle className="w-4 h-4" /> What We Do Not Do
            </div>
            <h3 className="text-2xl font-black text-[#0e1116]">No Field Surveying</h3>
            <p className="text-gray-700 mt-3 leading-relaxed">
              We do not perform field surveys, set monuments, or hold a state PLS license. Field work, certifications, and seals remain with you — we are a drafting back-office that scales your firm&apos;s CAD output.
            </p>
          </div>
        </div>
      </section>

      {/* WHY ACCURATE CAD DRAFTING MATTERS */}
      <section className="py-20 bg-gray-50 relative overflow-hidden" data-testid="ls-why-matters">
        <ServiceBackdrop variant="survey" theme="light" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel>Why Accurate CAD Drafting Matters</SectionLabel>
            <h2 className="text-4xl font-black text-[#0e1116]">Precision <span className="text-green-600">protects every dollar</span> on your project</h2>
            <p className="text-gray-500 mt-3">From property closings to permit submittals to construction hand-off — accurate CAD drafts are the foundation of every survey-driven decision.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_MATTERS.map(m => (
              <div key={m.num} className="bg-white rounded-lg border border-gray-200 p-6 hover:border-green-500 hover:shadow-lg transition-all">
                <div className="text-5xl font-black text-orange-500 leading-none">{m.num}</div>
                <h3 className="font-bold text-lg text-[#0e1116] mt-4">{m.title}</h3>
                <p className="text-gray-600 text-sm mt-2 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAD DRAFTING FOR EVERY SURVEY TYPE */}
      <section className="py-20 bg-white relative overflow-hidden" data-testid="ls-survey-types">
        <ServiceBackdrop variant="survey" theme="light" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel>CAD Drafting For Every Survey Type</SectionLabel>
            <h2 className="text-4xl font-black text-[#0e1116]">Every survey type. <span className="text-orange-500">One drafting partner.</span></h2>
            <p className="text-gray-500 mt-3">Send us your field data — we draft every type of land survey deliverable: boundary, topographic, ALTA, as-built, ROW, subdivision plats, towers, pipelines, and more.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SURVEY_TYPES.map(s => (
              <Link key={s.slug} to={`/services/land-surveying/${s.slug}`} data-testid={`ls-card-${s.slug}`} className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-green-500 hover:shadow-xl hover:-translate-y-1 transition-all">
                <div className="relative h-40 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e1116]/85 via-[#0e1116]/30 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 inline-flex items-center gap-2">
                    <span className="w-9 h-9 bg-orange-500 rounded flex items-center justify-center shadow-lg">
                      <Icon name={s.icon} className="w-5 h-5 text-white" />
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-lg text-[#0e1116] group-hover:text-green-600 transition-colors">{s.title}</h3>
                  <p className="text-gray-500 text-sm mt-2 line-clamp-3">{s.short}</p>
                  <div className="flex items-center gap-1 mt-4 text-green-600 group-hover:text-orange-500 text-xs font-bold uppercase tracking-wider">
                    Learn More <Icons.ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-20 bg-[#0e1116] text-white relative overflow-hidden" data-testid="ls-why-choose">
        <ServiceBackdrop variant="survey" accent="#94a3b8" accentAlt="#f97316" />
        <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px]"></div>
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel>Why Choose Land Drafters?</SectionLabel>
            <h2 className="text-4xl font-black">The <span className="text-green-500">drafting partner</span> licensed surveyors trust</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map((w, i) => (
              <div key={w.title} className="bg-[#1a1f2e] border border-gray-900/40 rounded-lg p-6 hover:border-orange-500 transition-all">
                <div className={`w-12 h-12 rounded ${i % 2 === 0 ? 'bg-gray-600' : 'bg-orange-500'} text-white flex items-center justify-center`}>
                  <Icon name={w.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-lg mt-4">{w.title}</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR STREAMLINED PROCESS */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden" data-testid="ls-process">
        <ServiceBackdrop variant="survey" theme="light" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel>Our Streamlined Process</SectionLabel>
            <h2 className="text-4xl font-black text-[#0e1116]">From field data to <span className="text-orange-500">sealed-ready</span> in 5 steps</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {SURVEY_PROCESS.map(p => (
              <div key={p.num} className="bg-white rounded-lg border border-gray-200 p-5 hover:border-green-500 hover:shadow-lg transition-all">
                <div className="text-3xl font-black text-green-600">{p.num}</div>
                <h3 className="font-bold text-[#0e1116] mt-2">{p.title}</h3>
                <p className="text-gray-500 text-xs mt-2 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES WE SERVE */}
      <section className="py-20 bg-white relative overflow-hidden" data-testid="ls-industries">
        <ServiceBackdrop variant="survey" theme="light" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel>Industries We Serve</SectionLabel>
            <h2 className="text-4xl font-black text-[#0e1116]">Survey CAD drafting <span className="text-green-600">across every sector</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {SURVEY_INDUSTRIES.map(ind => (
              <div key={ind.title} className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:bg-[#0e1116] hover:text-white hover:border-green-500 transition-all group">
                <div className="w-12 h-12 rounded bg-gray-100 group-hover:bg-gray-600 text-green-700 group-hover:text-white flex items-center justify-center transition-colors">
                  <Icon name={ind.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-bold mt-4 text-[#0e1116] group-hover:text-white">{ind.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-orange-300 mt-2 leading-relaxed">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DATA INPUTS & CAD STACK */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden" data-testid="ls-data-inputs">
        <ServiceBackdrop variant="survey" theme="light" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel>Data Inputs &amp; CAD Stack</SectionLabel>
            <h2 className="text-4xl font-black text-[#0e1116]">We draft from the data <span className="text-orange-500">your crew already collects</span></h2>
            <p className="text-gray-500 mt-3">No field work on our side. Send the inputs — we deliver the CAD.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SURVEY_DATA_INPUTS.map(d => (
              <div key={d.title} className="bg-white border border-gray-200 rounded p-4 hover:border-green-500 transition-all">
                <Icon name={d.icon} className="w-5 h-5 text-orange-500" />
                <h4 className="font-bold text-sm text-[#0e1116] mt-2">{d.title}</h4>
                <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-[#0e1116] text-white rounded-xl p-7">
            <div className="text-orange-400 text-[10px] uppercase tracking-[0.25em] font-bold mb-3 flex items-center gap-2">
              <Icons.Cpu className="w-3.5 h-3.5" /> Technology Stack
            </div>
            <div className="flex flex-wrap gap-2">
              {SURVEY_TECH_STACK.map(t => (
                <span key={t} className="text-xs bg-gray-900/40 border border-gray-700/50 text-green-100 px-3 py-1.5 rounded-full">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RECENT PROJECT HIGHLIGHTS */}
      <section className="py-20 bg-white relative overflow-hidden" data-testid="ls-projects">
        <ServiceBackdrop variant="survey" theme="light" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel>Recent Project Highlights</SectionLabel>
            <h2 className="text-4xl font-black text-[#0e1116]">See how we <span className="text-green-600">deliver results</span></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {SURVEY_PROJECTS.map(p => (
              <div key={p.title} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-7 hover:border-green-500 hover:shadow-lg transition-all">
                <div className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold">{p.category}</div>
                <h3 className="text-xl font-black text-[#0e1116] mt-2">{p.title}</h3>
                <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Challenge</div>
                    <p className="text-gray-600 mt-1">{p.challenge}</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Result</div>
                    <p className="text-gray-600 mt-1">{p.result}</p>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-3 gap-3 pt-5 border-t border-gray-200">
                  {p.metrics.map(m => (
                    <div key={m.l} className="text-center">
                      <div className="text-2xl font-black text-green-600">{m.v}</div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{m.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* START YOUR PROJECT TODAY */}
      <section className="py-20 bg-gradient-to-br from-[#0e1116] via-gray-950 to-[#0e1116] text-white relative overflow-hidden" data-testid="ls-cta">
        <ServiceBackdrop variant="survey" accent="#94a3b8" accentAlt="#f97316" />
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <SectionLabel>Start Your Project Today!</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            Send us your <span className="text-green-500">field data</span> — we&apos;ll deliver <span className="text-orange-500">survey-grade CAD drafts</span> in 24–48 hours
          </h2>
          <p className="text-gray-300 mt-5 max-w-2xl mx-auto">White-label, confidential, and ready for your seal.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/get-quote" data-testid="ls-cta-quote" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded uppercase text-sm tracking-wider transition-all">
              Get a Quote <Icons.ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-500 hover:border-orange-500 hover:text-orange-400 text-white font-bold px-8 py-4 rounded uppercase text-sm tracking-wider transition-all">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <FloatingActions />
    </>
  );
};

// ===== SUB-PAGE FOR EACH SURVEY TYPE =====

export const SurveyTypeDetail = () => {
  const { subSlug } = useParams();
  const s = SURVEY_TYPES.find(x => x.slug === subSlug);
  const [openFaq, setOpenFaq] = useState(0);
  useSeo({
    title: s ? `${s.title} CAD Drafting` : 'Survey CAD Drafting',
    description: s ? (s.intro || `${s.title} CAD drafting services from Land Drafters — fast, accurate, ISO 9001:2015 certified.`) : 'Survey CAD drafting services from Land Drafters.',
    path: `/services/land-surveying/${subSlug}`
  });
  if (!s) return <Navigate to="/services/land-surveying" replace />;

  const others = SURVEY_TYPES.filter(x => x.slug !== s.slug);

  return (
    <>
      <section className="relative pt-20 pb-12 bg-[#0e1116] text-white overflow-hidden" data-testid="sst-hero">
        <DotConnect density={50} color="#94a3b8" lineColor="rgba(148,163,184,0.85)" accentColor="#f97316" maxDistance={130} />
        <ServiceBackdrop slug={s.slug} accent="#94a3b8" accentAlt="#f97316" />
        <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px]"></div>
        <div className="relative max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs text-gray-400 mb-4 flex items-center gap-2 flex-wrap">
              <Link to="/" className="hover:text-green-400">Home</Link>
              <Icons.ChevronRight className="w-3 h-3" />
              <Link to="/services" className="hover:text-green-400">Services</Link>
              <Icons.ChevronRight className="w-3 h-3" />
              <Link to="/services/land-surveying" className="hover:text-green-400">Land Surveying CAD Drafting Drafting</Link>
              <Icons.ChevronRight className="w-3 h-3" />
              <span className="text-orange-400 font-bold">{s.title}</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-orange-900/40 border border-orange-700/50 text-orange-300 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded mb-4">
              <Icon name={s.icon} className="w-4 h-4" /> CAD Drafting Only
            </div>
            <h1 className="text-4xl md:text-5xl font-black leading-[1.05]">
              {s.title.split(' ').slice(0, -1).join(' ') || s.title}{' '}
              <span className="text-green-500">{s.title.split(' ').slice(-1)[0]}</span>
            </h1>
            <p className="text-gray-300 mt-5 max-w-xl leading-relaxed">{s.short}</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/get-quote" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider" data-testid="sst-quote">
                Get Quote <Icons.ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-500 hover:border-green-500 hover:text-green-400 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider">
                Contact Us
              </Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative rounded-lg overflow-hidden border border-gray-900/50">
              <img src={s.image} alt={s.title} className="w-full h-[380px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0e1116]/70 to-transparent"></div>
              <div className="absolute top-4 left-4 bg-[#0e1116]/80 backdrop-blur-sm border border-gray-700/40 rounded px-3 py-1.5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold">Quick Contact</div>
                <div className="text-xs text-white mt-1">(+1) 786-673-2523</div>
                <div className="text-xs text-white">+91 9960 250 279</div>
                <div className="text-xs text-green-300">landdrafters@gmail.com</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 bg-white relative overflow-hidden" data-testid="sst-features">
        <ServiceBackdrop slug={s.slug} theme="light" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-10">
            <SectionLabel>Key Features</SectionLabel>
            <h2 className="text-3xl font-black text-[#0e1116]">Built for <span className="text-green-600">{s.title}</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {s.features.map(f => (
              <div key={f.title} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-5 hover:border-green-500 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded bg-orange-500 text-white flex items-center justify-center">
                  <Icons.CheckCircle2 className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-[#0e1116] mt-3">{f.title}</h3>
                <p className="text-gray-500 text-xs mt-1.5">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview + Deliverables + FAQ */}
      <section className="py-16 bg-gray-50 relative overflow-hidden">
        <ServiceBackdrop slug={s.slug} theme="light" />
        <div className="relative max-w-[1200px] mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <SectionLabel>Overview</SectionLabel>
            <h2 className="text-3xl font-black text-[#0e1116]">{s.title} <span className="text-orange-500">CAD Drafting</span></h2>
            <p className="text-gray-700 mt-4 leading-relaxed">{s.overview}</p>

            <h3 className="font-black text-xl text-[#0e1116] mt-10 mb-4">Key Deliverables</h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              {s.deliverables.map(d => (
                <li key={d} className="flex gap-2 text-sm text-gray-700">
                  <Icons.CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" /> {d}
                </li>
              ))}
            </ul>

            <h3 className="font-black text-xl text-[#0e1116] mt-10 mb-4">FAQ</h3>
            <div className="space-y-2">
              {s.faqs.map((f, i) => (
                <div key={f.q} className={`bg-white rounded-lg border ${openFaq === i ? 'border-gray-500 shadow-md' : 'border-gray-200 hover:border-green-300'}`} data-testid={`sst-faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full text-left px-5 py-4 flex items-start justify-between gap-3">
                    <span className="font-bold text-[#0e1116] text-sm">{f.q}</span>
                    <Icons.ChevronDown className={`w-4 h-4 text-green-600 shrink-0 mt-1 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && <div className="px-5 pb-4 text-gray-600 text-sm border-t border-gray-100 pt-3">{f.a}</div>}
                </div>
              ))}
            </div>
          </div>

          <aside className="space-y-5 self-start lg:sticky lg:top-28">
            <div className="bg-gradient-to-br from-gray-700 to-gray-600 text-white rounded-lg p-6">
              <h4 className="font-black text-lg">Ready to Get Started?</h4>
              <p className="text-green-100 text-sm mt-2">Submit your project details and get a free quote within 24 hours.</p>
              <Link to="/get-quote" className="inline-flex items-center gap-2 mt-5 bg-white text-green-700 hover:bg-orange-500 hover:text-white font-bold px-5 py-3 rounded text-xs uppercase tracking-wider transition-colors">Request Quote <Icons.ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="font-bold text-[#0e1116]">Other Survey Types</h4>
              <ul className="mt-4 space-y-2.5">
                {others.map(o => (
                  <li key={o.slug}>
                    <Link to={`/services/land-surveying/${o.slug}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-green-600">
                      <Icons.ChevronRight className="w-4 h-4 text-orange-500" /> {o.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-white relative overflow-hidden" data-testid="sst-process">
        <ServiceBackdrop slug={s.slug} theme="light" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-10">
            <SectionLabel>Our Process</SectionLabel>
            <h2 className="text-3xl font-black text-[#0e1116]">A clear, structured workflow</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {SURVEY_PROCESS.map(p => (
              <div key={p.num} className="bg-gray-50 rounded-lg border border-gray-200 p-5 hover:border-green-500 transition-all">
                <div className="text-3xl font-black text-green-600">{p.num}</div>
                <h3 className="font-bold text-[#0e1116] mt-2">{p.title}</h3>
                <p className="text-gray-500 text-xs mt-2">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <ServiceBackdrop slug={s.slug} theme="light" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-10">
            <SectionLabel>Getting Started</SectionLabel>
            <h2 className="text-3xl font-black text-[#0e1116]">To ensure smooth project initiation</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {SURVEY_GETTING_STARTED.map(g => (
              <div key={g.title} className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-bold text-[#0e1116]">{g.title}</h4>
                <ul className="mt-4 space-y-2 text-sm text-gray-600">
                  {g.items.map(it => (
                    <li key={it} className="flex items-start gap-2"><Icons.Check className="w-3.5 h-3.5 text-green-600 shrink-0 mt-1" /> {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-[#0e1116] text-white rounded-xl p-7">
            <div className="text-orange-400 text-[10px] uppercase tracking-[0.25em] font-bold mb-3 flex items-center gap-2">
              <Icons.Cpu className="w-3.5 h-3.5" /> Technology
            </div>
            <div className="flex flex-wrap gap-2">
              {SURVEY_TECH_STACK.map(t => (
                <span key={t} className="text-xs bg-gray-900/40 border border-gray-700/50 text-green-100 px-3 py-1.5 rounded-full">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-700 to-gray-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-black">Let&apos;s Work Together on Your {s.title} Project!</h2>
          <p className="text-green-100 mt-4 max-w-2xl mx-auto">Partner with Land Drafters for accurate, reliable {s.title.toLowerCase()} CAD drafting services. Our experienced team delivers precision drawings that meet industry standards.</p>
          <ul className="grid sm:grid-cols-2 gap-2 mt-6 max-w-xl mx-auto text-left">
            {[`Expert ${s.title} drafting`, 'Quick 24-48 hour turnaround', 'Industry-standard CAD files', '100% satisfaction guarantee'].map(t => (
              <li key={t} className="flex gap-2 text-green-100"><Icons.CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" /> {t}</li>
            ))}
          </ul>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/get-quote" className="inline-flex items-center gap-2 bg-white text-green-700 hover:bg-orange-500 hover:text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider transition-colors">Get Free Quote <Icons.ArrowRight className="w-4 h-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border-2 border-white hover:bg-white hover:text-green-700 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>

      <CTA />
      <FloatingActions />
    </>
  );
};

export default LandSurveyingPage;
